'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Bone, Clock, Shield, CheckCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { useLocalizedNumbers } from '@/hooks/useLocalizedNumbers';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

export default function BoneGraftingSection() {
    const { t } = useTranslation('dental_implants');
    const { isRTL } = useRTL();
    const { localizeText } = useLocalizedNumbers();
    const [expandedSection, setExpandedSection] = useState<string | null>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    const sections = [
        {
            id: 'what-is',
            title: localizeText(t('bone_grafting.sections.what_is.title')),
            content: localizeText(t('bone_grafting.sections.what_is.content'))
        },
        {
            id: 'when-needed',
            title: localizeText(t('bone_grafting.sections.when_needed.title')),
            content: localizeText(t('bone_grafting.sections.when_needed.content'))
        },
        {
            id: 'materials',
            title: localizeText(t('bone_grafting.sections.materials.title')),
            content: localizeText(t('bone_grafting.sections.materials.content'))
        },
        {
            id: 'process',
            title: localizeText(t('bone_grafting.sections.process.title')),
            content: localizeText(t('bone_grafting.sections.process.content'))
        },
        {
            id: 'recovery',
            title: localizeText(t('bone_grafting.sections.recovery.title')),
            content: localizeText(t('bone_grafting.sections.recovery.content'))
        }
    ];

    const benefits = [
        localizeText(t('bone_grafting.benefits.stability')),
        localizeText(t('bone_grafting.benefits.prevent_loss')),
        localizeText(t('bone_grafting.benefits.facial_support')),
        localizeText(t('bone_grafting.benefits.challenging_cases'))
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(contentRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: "top 70%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

            // Image animation
            if (imageRef.current) {
                gsap.fromTo(imageRef.current,
                    { opacity: 0, scale: 0.9 },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 1,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: imageRef.current,
                            start: "top 80%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const toggleSection = (sectionId: string) => {
        setExpandedSection(expandedSection === sectionId ? null : sectionId);
    };

    return (
        <section
            id="bone-grafting"
            ref={sectionRef}
            className="py-14 bg-slate-50 dark:bg-navy-950"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className={cn(
                        "text-4xl md:text-5xl font-bold text-navy dark:text-white mb-6 font-serif",
                        isRTL && "font-arabic"
                    )}>
                        {localizeText(t('bone_grafting.title_part1'))}{' '}
                        <span className="text-gold">{localizeText(t('bone_grafting.title_part2'))}</span>
                    </h2>
                    <p className={cn(
                        "text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed",
                        isRTL && "font-arabic"
                    )}>
                        {localizeText(t('bone_grafting.description'))}
                    </p>
                </div>

                {/* Bone Grafting Image */}
                <div className="mb-16">
                    <div ref={imageRef} className="flex justify-center">
                        <div className="relative max-w-2xl w-full">
                            <Image
                                src="/images/treatments/implant/dental-bone-graft.webp"
                                alt={t('bone_grafting.image_alt')}
                                width={600}
                                height={400}
                                className="w-full h-auto rounded-2xl shadow-xl"
                                priority
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl pointer-events-none"></div>
                        </div>
                    </div>
                </div>

                <div ref={contentRef} className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-start">

                        {/* Left: Benefits & Timeline */}
                        <div className="space-y-8">
                            {/* Benefits */}
                            <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg dark:border dark:border-slate-700">
                                <div className={cn(
                                    "flex items-center mb-6",
                                    isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                                )}>
                                    <Bone className="w-8 h-8 text-gold" />
                                    <h3 className={cn(
                                        "text-2xl font-bold text-navy dark:text-white",
                                        isRTL && "font-arabic"
                                    )}>
                                        {localizeText(t('bone_grafting.why_choose.title'))}
                                    </h3>
                                </div>
                                <div className="space-y-4">
                                    {benefits.map((benefit, index) => (
                                        <div key={index} className={cn(
                                            "flex items-start",
                                            isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                                        )}>
                                            <CheckCircle className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                                            <span className={cn(
                                                "text-slate-600 dark:text-slate-300",
                                                isRTL && "font-arabic"
                                            )}>{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="bg-gradient-to-br from-gold/10 to-gold/5 rounded-2xl p-8">
                                <div className={cn(
                                    "flex items-center justify-center mb-6",
                                    isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                                )}>
                                    <Clock className="w-8 h-8 text-gold" />
                                    <h3 className={cn(
                                        "text-xl font-bold text-navy dark:text-white",
                                        isRTL && "font-arabic"
                                    )}>
                                        {localizeText(t('bone_grafting.timeline.title'))}
                                    </h3>
                                </div>
                                <div className="space-y-4">
                                    <div className={cn(
                                        "flex items-center",
                                        isRTL ? "space-x-reverse space-x-4" : "space-x-4"
                                    )}>
                                        <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold text-sm">
                                            {localizeText("1")}
                                        </div>
                                        <div className={isRTL ? "text-right" : "text-left"}>
                                            <div className={cn(
                                                "font-semibold text-navy dark:text-white",
                                                isRTL && "font-arabic"
                                            )}>{localizeText(t('bone_grafting.timeline.step1.title'))}</div>
                                            <div className={cn(
                                                "text-sm text-slate-600 dark:text-slate-300",
                                                isRTL && "font-arabic"
                                            )}>{localizeText(t('bone_grafting.timeline.step1.duration'))}</div>
                                        </div>
                                    </div>
                                    <div className={cn(
                                        "flex items-center",
                                        isRTL ? "space-x-reverse space-x-4" : "space-x-4"
                                    )}>
                                        <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold text-sm">
                                            {localizeText("2")}
                                        </div>
                                        <div className={isRTL ? "text-right" : "text-left"}>
                                            <div className={cn(
                                                "font-semibold text-navy dark:text-white",
                                                isRTL && "font-arabic"
                                            )}>{localizeText(t('bone_grafting.timeline.step2.title'))}</div>
                                            <div className={cn(
                                                "text-sm text-slate-600 dark:text-slate-300",
                                                isRTL && "font-arabic"
                                            )}>{localizeText(t('bone_grafting.timeline.step2.duration'))}</div>
                                        </div>
                                    </div>
                                    <div className={cn(
                                        "flex items-center",
                                        isRTL ? "space-x-reverse space-x-4" : "space-x-4"
                                    )}>
                                        <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold text-sm">
                                            {localizeText("3")}
                                        </div>
                                        <div className={isRTL ? "text-right" : "text-left"}>
                                            <div className={cn(
                                                "font-semibold text-navy dark:text-white",
                                                isRTL && "font-arabic"
                                            )}>{localizeText(t('bone_grafting.timeline.step3.title'))}</div>
                                            <div className={cn(
                                                "text-sm text-slate-600 dark:text-slate-300",
                                                isRTL && "font-arabic"
                                            )}>{localizeText(t('bone_grafting.timeline.step3.duration'))}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Accordion with Fixed Height */}
                        <div className="space-y-4">
                            <h3 className={cn(
                                "text-2xl font-bold text-navy dark:text-white mb-6",
                                isRTL && "font-arabic"
                            )}>
                                {localizeText(t('bone_grafting.common_questions.title'))}
                            </h3>
                            <div className="space-y-4 h-[600px] overflow-y-auto pr-2">
                                {sections.map((section) => (
                                    <div
                                        key={section.id}
                                        className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg dark:border dark:border-slate-700 overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleSection(section.id)}
                                            className={cn(
                                                "w-full p-6 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-navy-950 transition-colors",
                                                isRTL ? "text-right" : "text-left"
                                            )}
                                        >
                                            <h4 className={cn(
                                                "text-lg font-semibold text-navy dark:text-white",
                                                isRTL && "font-arabic"
                                            )}>
                                                {section.title}
                                            </h4>
                                            {expandedSection === section.id ? (
                                                <ChevronUp className="w-5 h-5 text-gold" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                                            )}
                                        </button>

                                        <AnimatePresence>
                                            {expandedSection === section.id && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="border-t border-slate-200 dark:border-slate-700"
                                                >
                                                    <div className="p-6 pt-4">
                                                        <p className={cn(
                                                            "text-slate-600 dark:text-slate-300 leading-relaxed",
                                                            isRTL && "font-arabic text-right"
                                                        )}>
                                                            {section.content}
                                                        </p>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* CTA Section - Full Width Below */}
                    <div>
                        <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg dark:border dark:border-slate-700 text-center max-w-2xl mx-auto">
                            <Shield className="w-12 h-12 text-gold mx-auto mb-4" />
                            <h3 className={cn(
                                "text-xl font-bold text-navy dark:text-white mb-4",
                                isRTL && "font-arabic"
                            )}>
                                {localizeText(t('bone_grafting.cta.title'))}
                            </h3>
                            <p className={cn(
                                "text-slate-600 dark:text-slate-300 mb-4",
                                isRTL && "font-arabic"
                            )}>
                                {localizeText(t('bone_grafting.cta.description'))}
                            </p>
                            <Button
                                size="lg"
                                className={cn(
                                    "bg-gold hover:bg-gold text-navy px-8 py-3 rounded-xl font-semibold transition-all duration-300 inline-flex items-center gap-2",
                                    isRTL && "font-arabic"
                                )}
                            >
                                {localizeText(t('bone_grafting.cta.button'))}
                                {isRTL ? (
                                    <ArrowLeft className="w-4 h-4" />
                                ) : (
                                    <ArrowRight className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
} 
